/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ClipboardList, Eye, Package, RefreshCw, Search, X } from "lucide-react";
import CommerceItemImage from "../../../../components/store/CommerceItemImage.jsx";
import { SelectDropdown, StatusBadge } from "../../../../components/ui/index.js";
import PaymentReviewPanel from "./PaymentReviewPanel.jsx";
import BizumReceiverCard from "./BizumReceiverCard.jsx";
import FulfillmentPanel from "./FulfillmentPanel.jsx";
import { useSearchParams } from "react-router-dom";

const ORDER_STATUSES = [
    ["pendiente_pago", "Pendiente de pago"],
    ["confirmado", "Confirmado"],
    ["en_preparacion", "En preparación"],
    ["enviado", "Enviado"],
    ["entregado", "Entregado"],
    ["cancelado", "Cancelado"],
    ["incidencia", "Incidencia"],
];

const STATUS_BADGE = {
    pendiente_pago: "badge-warning",
    confirmado: "badge-info",
    en_preparacion: "badge-info",
    enviado: "badge-primary",
    entregado: "badge-success",
    cancelado: "badge-error",
    incidencia: "badge-error",
};

// Mismo criterio que UserOrderDetail.jsx (perfil): el fulfillment es
// independiente por línea, nunca se resume en un único estado de pedido.
export const FULFILLMENT_TONE = {
    pendiente_pago: "neutral",
    pendiente_preparacion: "warning",
    pendiente_produccion: "warning",
    enviado: "info",
    entregado: "success",
    pendiente_habilitacion: "warning",
    disponible: "success",
    descargado: "success",
    cancelado: "error",
    incidencia: "error",
    revocado: "error",
};

const fmt = (value, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(value || 0));

const statusLabel = (status, fallback) =>
    fallback || ORDER_STATUSES.find(([value]) => value === status)?.[1] || status;

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
};

function OrderDetail({ order, loading, onClose, onPatch, onOrderReplaced }) {
    const [notes, setNotes] = useState(order?.internal_notes || "");

    useEffect(() => {
        setNotes(order?.internal_notes || "");
    }, [order?.internal_notes, order?.order_number]);

    if (!order && !loading) {
        return (
            <aside className="glassCard rounded-xl p-4 min-h-80 flex flex-col items-center justify-center gap-2 text-center text-base-content/60">
                <ClipboardList className="w-10 h-10 opacity-30" />
                <p>Selecciona un pedido para ver el detalle.</p>
            </aside>
        );
    }

    return (
        <aside className="glassCard rounded-xl p-4 flex flex-col gap-4 min-h-80">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="font-bold text-lg font-mono">{order?.order_number || "Pedido"}</h3>
                    {order?.created_at && (
                        <p className="text-xs text-base-content/55">
                            {new Date(order.created_at).toLocaleString("es-ES")}
                        </p>
                    )}
                </div>
                <button className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>
                    <X className="w-4 h-4" />
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="form-control gap-1">
                            <span className="label-text text-xs font-semibold">Estado logístico</span>
                            <select
                                className="select select-bordered select-sm"
                                value={order.status}
                                onChange={(event) => onPatch({ status: event.target.value })}
                            >
                                {ORDER_STATUSES.map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>
                        <div className="rounded-lg bg-base-200/60 p-3">
                            <p className="text-xs text-base-content/55">Cliente</p>
                            <p className="text-sm font-medium">{order.shipping_name}</p>
                            <p className="text-xs text-base-content/65">{order.contact_email}</p>
                            {order.contact_phone && <p className="text-xs text-base-content/65">{order.contact_phone}</p>}
                        </div>
                    </div>

                    <section>
                        <h4 className="font-semibold mb-2">Artículos</h4>
                        <ul className="flex flex-col gap-2">
                            {(order.items || []).map((item) => (
                                <li key={item.id} className="flex items-center gap-3 rounded-lg bg-base-100/70 border border-base-300 p-2">
                                    <CommerceItemImage item={item} alt={item.snapshot_name} className="w-14 h-14" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-medium truncate">{item.snapshot_name}</p>
                                            <StatusBadge
                                                label={item.fulfillment_status_display || item.fulfillment_status}
                                                tone={FULFILLMENT_TONE[item.fulfillment_status] || "neutral"}
                                                size="xs"
                                            />
                                        </div>
                                        <p className="text-xs text-base-content/60">
                                            {item.item_type === "custom_order"
                                                ? `${item.quantity} uds. · ${item.snapshot_material} · ${item.snapshot_color}${item.snapshot_finish ? ` · ${item.snapshot_finish}` : ""}`
                                                : `${item.quantity} x ${fmt(item.snapshot_unit_price, item.snapshot_currency)}`}
                                        </p>
                                        {item.item_type === "custom_order" && item.custom_order_request_detail?.customized_keyring && (
                                            <p className="text-xs text-base-content/45">
                                                Diseño aprobado: {item.custom_order_request_detail.customized_keyring.name}
                                            </p>
                                        )}
                                    </div>
                                    <p className="font-semibold whitespace-nowrap">{fmt(item.snapshot_line_total, item.snapshot_currency)}</p>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <FulfillmentPanel
                        order={order}
                        onOrderUpdated={onOrderReplaced}
                    />

                    <section className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-base-200/60 p-3">
                            <p className="text-xs text-base-content/55 mb-1">Envío</p>
                            <p>{order.shipping_address_line1}</p>
                            {order.shipping_address_line2 && <p>{order.shipping_address_line2}</p>}
                            <p>{order.shipping_postal_code} {order.shipping_city}</p>
                            {order.shipping_state && <p>{order.shipping_state}</p>}
                            <p>{order.shipping_country}</p>
                        </div>
                        <div className="rounded-lg bg-base-200/60 p-3">
                            <p className="text-xs text-base-content/55 mb-1">Totales</p>
                            <div className="flex justify-between"><span>Subtotal</span><span>{fmt(order.subtotal, order.currency)}</span></div>
                            <div className="flex justify-between"><span>Envío</span><span>{fmt(order.shipping_cost, order.currency)}</span></div>
                            <div className="flex justify-between font-bold mt-1"><span>Total</span><span>{fmt(order.total_price, order.currency)}</span></div>
                        </div>
                    </section>

                    <PaymentReviewPanel
                        key={order.order_number}
                        orderNumber={order.order_number}
                    />

                    <label className="form-control gap-1">
                        <span className="label-text text-xs font-semibold">Notas internas</span>
                        <textarea
                            className="textarea textarea-bordered min-h-24"
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                            onBlur={() => onPatch({ internal_notes: notes })}
                            placeholder="Seguimiento, pago manual, producción..."
                        />
                    </label>
                </>
            )}
        </aside>
    );
}

export default function AdminOrders() {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("user_id") || "";
    const [orders, setOrders] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedLoading, setSelectedLoading] = useState(false);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get("/orders-admin/", {
                params: {
                    search: search || undefined,
                    status: statusFilter || undefined,
                    user_id: userId || undefined,
                },
            });
            const results = data.results ?? data;
            setOrders(Array.isArray(results) ? results : []);
            setTotalCount(data.count ?? results.length ?? 0);
        } catch (err) {
            console.error("Error fetching admin orders:", err);
            setError(formatApiError(err, "No se pudieron cargar los pedidos."));
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, userId]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const openOrder = async (orderNumber) => {
        setSelectedLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`/orders-admin/${orderNumber}/`);
            setSelectedOrder(data);
        } catch (err) {
            console.error("Error fetching order detail:", err);
            setError(formatApiError(err, "No se pudo cargar el detalle del pedido."));
        } finally {
            setSelectedLoading(false);
        }
    };

    const patchSelectedOrder = async (payload) => {
        if (!selectedOrder?.order_number) return;
        const previous = selectedOrder;
        setSelectedOrder((current) => ({ ...current, ...payload }));
        setOrders((current) => current.map((order) => (
            order.order_number === previous.order_number ? { ...order, ...payload } : order
        )));
        try {
            const { data } = await axios.patch(`/orders-admin/${previous.order_number}/`, payload);
            setSelectedOrder(data);
            setOrders((current) => current.map((order) => (
                order.order_number === data.order_number ? data : order
            )));
        } catch (err) {
            console.error("Error updating order:", err);
            setSelectedOrder(previous);
            setError(formatApiError(err, "No se pudo guardar el pedido."));
        }
    };

    // Las acciones de fulfillment (ship/deliver/incidencia/cancelar) ya
    // devuelven el pedido completo actualizado: no hace falta un PATCH extra.
    const replaceSelectedOrder = (updatedOrder) => {
        if (!updatedOrder) return;
        setSelectedOrder(updatedOrder);
        setOrders((current) => current.map((order) => (
            order.order_number === updatedOrder.order_number ? updatedOrder : order
        )));
    };

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            <BizumReceiverCard />

            <div className="glassCard rounded-xl p-3 z-40 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Package className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Pedidos</h2>
                            <p className="text-sm text-base-content/60">{totalCount} pedidos reales</p>
                        </div>
                    </div>
                    <button className="btn btn-sm btn-outline gap-2" onClick={fetchOrders}>
                        <RefreshCw className="w-4 h-4" />
                        Actualizar
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    <label className="input input-bordered input-sm flex items-center gap-2">
                        <Search className="w-4 h-4 opacity-60" />
                        <input
                            className="grow"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => event.key === "Enter" && fetchOrders()}
                            placeholder="Pedido, email, nombre"
                        />
                    </label>
                    <SelectDropdown
                        size="sm"
                        placeholder="Estado"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                            { value: "", label: "Todos" },
                            ...ORDER_STATUSES.map(([value, label]) => ({ value, label })),
                        ]}
                    />
                </div>
            </div>

            {error && (
                <div role="alert" className="alert alert-error shadow-lg">
                    <AlertTriangle className="w-5 h-5" />
                    <div>
                        <h3 className="font-semibold">Hay un problema con pedidos</h3>
                        <div className="text-sm">{error}</div>
                    </div>
                    <button className="btn btn-sm btn-ghost" onClick={fetchOrders}>Reintentar</button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_520px] gap-3 flex-1 min-h-0">
                <div className="glassCard rounded-xl overflow-auto scrollbar_mod">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <span className="loading loading-spinner loading-lg text-primary" />
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-2 text-center text-base-content/60">
                            <ClipboardList className="w-10 h-10 opacity-30" />
                            <p>No hay pedidos con estos filtros.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Pedido</th>
                                        <th>Cliente</th>
                                        <th>Items</th>
                                        <th>Estado</th>
                                        <th className="text-right">Total</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => (
                                        <tr key={order.id} className={selectedOrder?.id === order.id ? "bg-primary/10" : ""}>
                                            <td>
                                                <div className="font-mono font-semibold">{order.order_number}</div>
                                                <div className="text-xs text-base-content/50">
                                                    {new Date(order.created_at).toLocaleDateString("es-ES")}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="font-medium">{order.shipping_name}</div>
                                                <div className="text-xs text-base-content/60">{order.contact_email}</div>
                                                {order.user_email && (
                                                    <div className="text-xs text-base-content/40">Cuenta: {order.user_email}</div>
                                                )}
                                            </td>
                                            <td>{(order.items || []).length}</td>
                                            <td>
                                                <span className={`badge badge-sm ${STATUS_BADGE[order.status] || "badge-ghost"}`}>
                                                    {statusLabel(order.status, order.status_display)}
                                                </span>
                                            </td>
                                            <td className="text-right font-semibold">{fmt(order.total_price, order.currency)}</td>
                                            <td>
                                                <button className="btn btn-xs btn-outline gap-1" onClick={() => openOrder(order.order_number)}>
                                                    <Eye className="w-3 h-3" />
                                                    Ver
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <OrderDetail
                    order={selectedOrder}
                    loading={selectedLoading}
                    onClose={() => setSelectedOrder(null)}
                    onPatch={patchSelectedOrder}
                    onOrderReplaced={replaceSelectedOrder}
                />
            </div>
        </div>
    );
}
